/**
 * The OAuth popup's sign-off page.
 *
 * `window.close()` is best-effort — a browser may refuse it, and after a COOP browsing-context-group switch
 * (Shopify, Square) some engines refuse it outright. Rendering nothing in that case is the worst outcome
 * available: index.html paints a branded full-screen loader BEFORE any module runs, so the band is left
 * watching a spinner in a 520px window that never resolves, and after its 12s fallback a blank page. Booting
 * the SPA instead is worse still — that is the bug this whole change is about, an app homepage inside a
 * popup, a second session, and no way to tell it apart from a connect that went somewhere wrong.
 *
 * So a popup that is still on screen says so in words. Deliberately static DOM with literal colours rather
 * than React and tokens: it must render after `window.close()` has already been attempted, in a document
 * that is mid-teardown on the happy path, and nothing here is worth a component tree nobody will interact
 * with. Same reasoning as the pre-hydration loader island in index.html.
 */
export function renderOAuthPopupNotice(): void {
  if (typeof document === "undefined") return;

  // The branded loader is inline in index.html at z-index 999999 and would sit over this. Its own hide hook
  // fades it out properly; the removal is the belt for a document where the hook never got defined.
  try {
    (window as { acHideLoader?: () => void }).acHideLoader?.();
  } catch {
    /* the loader is decoration — never let it stop the notice */
  }
  document.getElementById("acLoader")?.remove();

  const params = new URLSearchParams(window.location.search);
  const ok = params.get("status") === "connected";

  document.title = ok ? "Connected" : "Not connected";
  document.body.innerHTML = "";

  const wrap = document.createElement("div");
  wrap.setAttribute("role", "status");
  wrap.style.cssText = [
    "position:fixed", "inset:0", "display:flex", "flex-direction:column",
    "align-items:center", "justify-content:center", "gap:8px", "padding:24px",
    "background:#efefee", "text-align:center",
    'font-family:"Inter",-apple-system,BlinkMacSystemFont,sans-serif',
  ].join(";");

  const heading = document.createElement("p");
  heading.textContent = ok ? "Connected." : "That didn’t connect.";
  heading.style.cssText = "margin:0;font-size:16px;font-weight:600;color:#1a1a1a;";

  const hint = document.createElement("p");
  // The app window has already been told the outcome over the channels in `oauthPopup.ts`, so this window
  // has no job left. Say that, rather than leaving a page that looks like it is still working.
  hint.textContent = "You can close this window.";
  hint.style.cssText = "margin:0;font-size:13px;color:#6b6b6b;";

  wrap.append(heading, hint);
  document.body.append(wrap);
}
